import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import MobileNav from "@/components/MobileNav";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import CookieConsent from "@/components/CookieConsent";
import AppDownloadBanner from "@/components/AppDownloadBanner";
import SiteFooter from "@/components/SiteFooter";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Explore from "@/pages/Explore";
import ActivityDetail from "@/pages/ActivityDetail";
import CreateActivity from "@/pages/CreateActivity";
import Profile from "@/pages/Profile";
import MyActivities from "@/pages/MyActivities";
import EmailHealth from "@/pages/EmailHealth";
import Notifications from "@/pages/Notifications";
import AuthCallback from "@/pages/AuthCallback";
import Pro from "@/pages/Pro";
import ProSuccess from "@/pages/ProSuccess";
import Analytics from "@/pages/Analytics";
import Leaderboard from "@/pages/Leaderboard";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Cookies from "@/pages/Cookies";
import Support from "@/pages/Support";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OpenInAppBanner from "@/components/OpenInAppBanner";
import ProRoute from "@/components/ProRoute";

function AppRouter() {
  const location = useLocation();
  // Handle Emergent OAuth callback that lands at any URL with #session_id=...
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
      <AppDownloadBanner />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateActivity /></ProtectedRoute>} />
        <Route path="/activity/:id/edit" element={<ProtectedRoute><CreateActivity /></ProtectedRoute>} />
        <Route path="/activity/:id" element={<ProtectedRoute><ActivityDetail /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/my-activities" element={<ProtectedRoute><MyActivities /></ProtectedRoute>} />
        <Route path="/admin/email-health" element={<EmailHealth />} />
        <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
        <Route path="/pro" element={<ProtectedRoute><Pro /></ProtectedRoute>} />
        <Route path="/pro/success" element={<ProtectedRoute><ProSuccess /></ProtectedRoute>} />
        <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/leaderboard" element={<ProtectedRoute><ProRoute feature="leaderboard"><Leaderboard /></ProRoute></ProtectedRoute>} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/support" element={<Support />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <SiteFooter />
      <MobileNav />
      <OpenInAppBanner />
      <PWAInstallPrompt />
      <CookieConsent />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "var(--tf-surface)", color: "var(--tf-text)", border: "1px solid var(--tf-border)" } }} />
            </BrowserRouter>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </div>
  );
}
